import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ArrowRight, Sparkles, Brain, Target, BookOpen, Instagram } from "lucide-react";

const FEATURES = [
  { icon: Target,    title: "Test My Elo",    to: "/test-elo", desc: "Eight puzzles at escalating difficulty. A quick, honest rating gauge — perfect for the Instagram tap-in.", tag: "Always free" },
  { icon: Sparkles,  title: "Infinite Puzzles", to: "/puzzles", desc: "Filter by theme (fork, pin, back-rank) and rating. Real positions from real games, indexed and tagged.", tag: "Always free" },
  { icon: Brain,     title: "Analyse My Game", to: "/analyze", desc: "Paste a PGN. Stockfish flags every blunder; Claude explains the reasoning in plain English — never inventing evaluations.", tag: "Always free" },
  { icon: BookOpen,  title: "Opening Traps",   to: "/traps",   desc: "Classic traps — Scholar's Mate, Légal's, Englund — auto-played with move-by-move commentary.", tag: "Always free" },
];

export default function Landing() {
  const { user } = useAuth();
  const authed = user && user !== false;
  const cta = authed ? "/dashboard" : "/register";

  return (
    <div>
      {/* Hero — plain black background, focused typography */}
      <section className="relative overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-16 sm:pt-24 sm:pb-32">
          <div className="max-w-3xl">
            <div className="overline mb-4">Companion to @instant_elo</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#EFE8D8]">
              Real puzzles.<br />
              Real engine review.<br />
              <span className="text-[#C6A15B] italic font-light">Real Elo.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#A8A196] max-w-2xl leading-relaxed">
              A quiet, focused chess trainer. Solve puzzles pulled from millions of real games, gauge your rating in seven positions, and let Stockfish plus a trained coach explain what went wrong in your last game.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={cta} data-testid="hero-cta-start" className="btn-brass">
                Start training <ArrowRight size={16} />
              </Link>
              <Link to="/traps" data-testid="hero-cta-traps" className="btn-ghost">
                Explore opening traps
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-[#756F67]">
              <span>Lichess puzzles</span>
              <span className="text-[#C6A15B]">•</span>
              <span>Stockfish 15</span>
              <span className="text-[#C6A15B]">•</span>
              <span>Claude coach</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="overline mb-3">The tools</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8] mb-12 max-w-2xl">
          Four workflows, one dark room to think.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => (
            <Link
              key={f.to}
              to={f.to}
              data-testid={`feature-card-${i}`}
              className="card-panel p-6 sm:p-8 hover:bg-[#25211B] hover:border-[#C6A15B]/40 transition-colors group block"
            >
              <div className="flex items-start justify-between mb-4">
                <f.icon size={22} className="text-[#C6A15B]" />
                <span className="text-xs font-mono uppercase tracking-widest text-[#756F67]">{f.tag}</span>
              </div>
              <h3 className="font-serif text-2xl text-[#EFE8D8] mb-3 group-hover:text-[#C6A15B] transition-colors">{f.title}</h3>
              <p className="text-sm text-[#A8A196] leading-relaxed">{f.desc}</p>
              <div className="mt-4 text-xs font-mono text-[#C6A15B] opacity-0 group-hover:opacity-100 transition-opacity">Open →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* About — made by the @instant_elo team */}
      <section className="border-t border-[rgba(239,232,216,0.08)] py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-6">
            <div>
              <div className="overline mb-2">About</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8]">Free. Forever. No sign‑up gates.</h2>
            </div>
            <a
              href="https://instagram.com/instant_elo"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-about-instagram"
              className="btn-brass self-start sm:self-end"
            >
              <Instagram size={16} /> Made by @instant_elo
            </a>
          </div>
          <p className="text-[#A8A196] max-w-2xl leading-relaxed">
            Instant Elo is a free companion to the @instant_elo Instagram — no ads, no paywalls, no premium tiers. Puzzles are pulled from lichess.org (CC0); game analysis runs on server‑side Stockfish; explanations are written by Claude, grounded on the engine's numbers. Enjoy.
          </p>
        </div>
      </section>

      <footer className="border-t border-[rgba(239,232,216,0.08)] py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-[#756F67] font-mono">
          <span>© 2026 Instant Elo</span>
          <a
            href="https://instagram.com/instant_elo"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram"
            className="inline-flex items-center gap-2 text-[#C6A15B] hover:text-[#D8B46C] transition-colors"
          >
            <Instagram size={14} /> @instant_elo
          </a>
          <span>Puzzles from lichess.org — CC0</span>
        </div>
      </footer>
    </div>
  );
}
